import React, { useMemo, useState } from "react"
import moment from "moment"

// UI imports
import Button from "../../../components/ui/button/button"
import Datepicker from "../../../components/ui/datepicker/datepicker"
import Input from "../../../components/ui/inputs/input"
import Table from "../../../components/structure/table/table"

// Audit pipeline (shared with the supplier audit)
import { loadSupplierAudit } from "../_utils/supplier-audit/load"
import { LINE_COLUMNS, buildBookingGroups } from "../_utils/supplier-audit/booking-lines"
import { downloadBookingWorkbook } from "../_utils/supplier-audit/booking-workbook"

const DEFAULT_START = moment("2027-01-01").startOf("day").valueOf()
const DEFAULT_LIMIT = "10"

/**
 * Preview cell in the same format the workbook uses.
 */
const renderCell = (value, format) => {
	if (value === "" || value === null || value === undefined) return <span className="db-exports-muted">-</span>
	if (value instanceof Date) return moment(value).format("DD/MM/YYYY")
	if (typeof value === "number") {
		if (format === "percent") return `${(value * 100).toFixed(1)}%`
		if (format === "money") return value.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
		return value.toLocaleString()
	}
	return String(value)
}

/**
 * "Booking export" tab - every booking travelling in the window laid out as
 * its supplier lines (hotels, rounds, transfer, flights) followed by a Total
 * row with the margins, with a grey row between bookings. Reuses the supplier
 * audit loader so the figures always agree with that export.
 */
export default function BookingExport() {
	const [startDate, setStartDate] = useState(DEFAULT_START)
	const [endDate, setEndDate] = useState(null)
	const [endPickerKey, setEndPickerKey] = useState(0)
	const [limit, setLimit] = useState(DEFAULT_LIMIT)

	const [loading, setLoading] = useState(false)
	const [progress, setProgress] = useState({ done: 0, total: 0 })
	const [error, setError] = useState("")
	const [result, setResult] = useState(null)
	const [downloading, setDownloading] = useState(false)

	const groups = useMemo(() => (result ? buildBookingGroups(result) : []), [result])

	const summary = useMemo(() => {
		if (!result) return null
		const lines = groups.flatMap((group) => group.lines)
		return {
			bookings: groups.length,
			lines: lines.length,
			unconfirmed: lines.filter((line) => line.supplierConfirmed === "No").length,
			mismatched: groups.filter((group) => group.total.check !== "OK").length,
		}
	}, [result, groups])

	const parsedLimit = Number.parseInt(limit, 10)
	const limitValue = Number.isFinite(parsedLimit) && parsedLimit > 0 ? parsedLimit : null

	const clearEndDate = () => {
		setEndDate(null)
		setEndPickerKey((key) => key + 1)
	}

	const generate = async () => {
		if (!startDate || loading) return

		setLoading(true)
		setError("")
		setResult(null)
		setProgress({ done: 0, total: 0 })

		try {
			const audit = await loadSupplierAudit({
				startMillis: moment(startDate).startOf("day").valueOf(),
				endMillis: endDate ? moment(endDate).endOf("day").valueOf() : null,
				limit: limitValue,
				onProgress: setProgress,
			})
			setResult(audit)
		} catch (err) {
			console.error("Error generating booking export:", err)
			setError("The export could not be generated. Check the console for details and try again.")
		} finally {
			setLoading(false)
		}
	}

	const download = async () => {
		if (!result || downloading) return

		const start = moment(result.window.startMillis).format("YYYY-MM-DD")
		const end = result.window.endMillis ? `_to_${moment(result.window.endMillis).format("YYYY-MM-DD")}` : "_onwards"
		const count = limitValue ? `_first_${groups.length}` : ""

		setDownloading(true)
		try {
			await downloadBookingWorkbook(result, `booking_export_${start}${end}${count}`)
		} catch (err) {
			console.error("Error writing booking export:", err)
			setError("The workbook could not be written. Check the console for details and try again.")
		} finally {
			setDownloading(false)
		}
	}

	return (
		<>
			<p className="db-exports-intro">
				Export every booking travelling from the chosen date as its supplier lines: hotels, rounds, transfer and flights with nights, tee times, players, confirmation and net cost, followed
				by a Total row holding the customer price, supplier costs, profit and commission. A second tab has one row per booking. Limit the number of bookings to try it on a few first.
			</p>

			<div className="page-filters db-exports-filters db-exports-audit-filters">
				<Datepicker
					value={startDate}
					onSelect={(date) => setStartDate(date)}
					placeholder="From (travel date)"
				/>

				<div className="db-exports-audit-end-date">
					<Datepicker
						key={endPickerKey}
						value={endDate}
						onSelect={(date) => setEndDate(date)}
						placeholder="To (optional)"
					/>
					{endDate && (
						<button
							type="button"
							className="db-exports-audit-clear"
							onClick={clearEndDate}>
							Clear
						</button>
					)}
				</div>

				<Input
					type="number"
					placeholder="Bookings (blank for all)"
					wrapperClass="db-exports-booking-limit"
					value={limit}
					onChange={setLimit}
				/>

				<Button
					filters={true}
					disabled={!startDate}
					label="Generate export"
					loadingText="Generating..."
					loading={loading}
					onClick={generate}
				/>

				<Button
					filters={true}
					disabled={!result || groups.length === 0}
					label="Download workbook"
					loadingText="Writing..."
					loading={downloading}
					onClick={download}
				/>
			</div>

			{loading && (
				<p className="db-exports-summary">
					Reading bookings... {progress.done}
					{progress.total > 0 ? ` / ${progress.total}` : ""}
				</p>
			)}

			{!loading && error && <p className="db-exports-summary db-exports-audit-error">{error}</p>}

			{!loading && summary && (
				<p className="db-exports-summary">
					<strong>{summary.bookings.toLocaleString()}</strong> booking{summary.bookings === 1 ? "" : "s"} with <strong>{summary.lines.toLocaleString()}</strong> supplier lines,{" "}
					{summary.unconfirmed.toLocaleString()} not confirmed by the supplier. {summary.mismatched.toLocaleString()} booking{summary.mismatched === 1 ? "" : "s"} where the lines do not match the
					saved margins.
				</p>
			)}

			{result && (
				<div className="db-exports-section">
					<div className="db-exports-section-header">
						<h2>Preview</h2>
					</div>

					<div className="db-exports-audit-preview">
						<Table
							className="db-exports-table db-exports-booking-table"
							headings={LINE_COLUMNS.map((column) => column.header)}
							noResults={groups.length === 0}
							noResultsMessage="No bookings travelling in this window">
							{groups.map((group, groupIndex) => (
								<React.Fragment key={group.booking.id}>
									{groupIndex > 0 && (
										<tr className="is-separator">
											<td colSpan={LINE_COLUMNS.length} />
										</tr>
									)}
									{[...group.lines, group.total].map((row, rowIndex) => (
										<tr
											key={rowIndex}
											className={row.type === "Total" ? "is-total" : ""}>
											{LINE_COLUMNS.map((column) => (
												<td
													key={column.key}
													className={column.key === "supplierConfirmed" && row[column.key] === "No" ? "is-unconfirmed" : ""}>
													{renderCell(row[column.key], column.format)}
												</td>
											))}
										</tr>
									))}
								</React.Fragment>
							))}
						</Table>
					</div>
				</div>
			)}
		</>
	)
}
